import { useEffect, useState, useRef, useContext, useMemo } from "react";

//Components
import SingleDropDown from "./SingleDropDown";
import MultiDropDownObjects from "./MultiDropDownObjects";
import CheckboxWithLabel from "./CheckboxWithLabel";
import Charge from "./Charge";
import CustomAutoComplete from "./CustomAutoComplete";
import AddressInputAutoSuggest from "./AddressInputAutoSuggest";
import {
  FormControl,
  Box,
  Button,
  Modal,
  TextField,
  Checkbox,
  MenuItem,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { UserContext } from "../Contexts/UserContext";
import { SetupContext } from "../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { MutationContext } from "../Contexts/MutationContext";

//Styling
import { useStyles } from "../Styles/styles";

//Utils
import {
  formatNameFromPersonObj,
  formatOfficerName,
  handleUuid,
  getAgeInYears,
  parseDateFlexible,
  parseObjectArrayIdString,
} from "../utils/utils";
import { shouldBeLocked } from "../utils/Roles";
import { getValueFromEventValue } from "../utils/getValueFromEventValue";
import { createEmptyArrestCharge } from "../utils/EmptyObjects";
import WrappedKeyboardDateTimePicker from "./WrappedKeyboardDateTimePicker";

import "./TicketAndWarning/IntentToSuspend/IntentToSuspend";
import {
  parseBacValue,
  shouldAutoCheckCommercialBacResult,
  shouldAutoCheckIntentBacResult,
} from "./TicketAndWarning/IntentToSuspend/intentToSuspendData";

const buildStreetLine = address => {
  if (!address) return "";
  return [
    address?.streetNumber,
    address?.streetName,
    address?.unit ? `#${address.unit}` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const getLocalMoment = value => {
  const parsed = parseDateFlexible(value);
  if (!parsed?.isValid?.()) return null;
  return parsed.clone().local();
};

const getInputDate = value => {
  const parsed = getLocalMoment(value);
  return parsed?.isValid?.() ? parsed.format("YYYY-MM-DD") : "";
};

const getInputTime = value => {
  const parsed = getLocalMoment(value);
  return parsed?.isValid?.() ? parsed.format("hh:mm") : "";
};

const getAmPm = value => {
  const parsed = getLocalMoment(value);
  return parsed?.isValid?.() ? parsed.format("A") : "AM";
};

const formatAge = age => (age === null || age < 0 ? "" : age.toString());

const getResolvedIntentToSuspendData = data => ({
  ...data,
  commercialAlcoholConcentration: data?.alcoholConcentration || "",
  testResultAtOrAboveLimit:
    !!data?.testResultAtOrAboveLimit || shouldAutoCheckIntentBacResult(data),
  commercialTestAtOrAboveLimit:
    !!data?.commercialTestAtOrAboveLimit ||
    shouldAutoCheckCommercialBacResult(data),
});

const getIntentValidationErrors = data => {
  const resolvedData = getResolvedIntentToSuspendData(data || {});
  const errors = [];
  const bac = parseBacValue(resolvedData?.alcoholConcentration);
  const hasBac = bac !== null;

  if (resolvedData?.bacWithPreviousConviction && (!hasBac || bac < 0.02)) {
    errors.push(
      "BAC with previous conviction requires a populated BAC of at least 0.02.",
    );
  }

  if (resolvedData?.testResultAtOrAboveLimit && (!hasBac || bac < 0.02)) {
    errors.push(
      "BAC test result checkbox requires a populated BAC of at least 0.02.",
    );
  }

  if (resolvedData?.refusedBreathOrBloodTest && hasBac) {
    errors.push("Breath/blood test refusal conflicts with a populated BAC.");
  }

  if (
    resolvedData?.refusedBreathOrBloodTest &&
    resolvedData?.testResultAtOrAboveLimit
  ) {
    errors.push("Breath/blood test refusal conflicts with BAC test result.");
  }

  if (
    (resolvedData?.commercialRefusal ||
      resolvedData?.commercialTestAtOrAboveLimit) &&
    !resolvedData?.isCommercialVehicle
  ) {
    errors.push(
      "Commercial BAC/refusal boxes require Driving Commercial Vehicle to be checked.",
    );
  }

  if (resolvedData?.commercialRefusal && hasBac) {
    errors.push("Commercial refusal conflicts with a populated BAC.");
  }

  if (
    resolvedData?.commercialRefusal &&
    resolvedData?.commercialTestAtOrAboveLimit
  ) {
    errors.push("Commercial refusal conflicts with commercial BAC test result.");
  }

  if (
    resolvedData?.commercialTestAtOrAboveLimit &&
    (!hasBac || bac < 0.04)
  ) {
    errors.push(
      "Commercial BAC test result requires a populated BAC of at least 0.04.",
    );
  }

  return errors;
};

const formatUserName = user => {
  const last = user?.lastName || "";
  const first = user?.firstName || "";
  const middle = user?.middleName || "";
  const full = [first, middle].filter(Boolean).join(" ").trim();
  if (last && full) return `${last}, ${full}`;
  if (last) return last;
  return full;
};

const formatIntentOfficerName = officer => {
  const personal = officer?.personalAttribute;

  if (personal) {
    return formatUserName({
      firstName: personal?.firstName,
      middleName: personal?.middleName,
      lastName: personal?.lastName,
    });
  }

  return officer?.name || formatUserName(officer);
};

const getIntentOfficerNumber = officer =>
  officer?.deptAttribute?.badge || officer?.badge || "";

const getIntentDefaults = ({
  incident,
  arresteePerson,
  arresteePis,
  arrest,
  user,
}) => {
  const personal = arresteePerson?.personalAttribute || {};
  const mailingAddress = personal?.mailingAddress || {};
  const intentOfficer = arrest?.arrestor || user;
  const dateOfBirthMoment = parseDateFlexible(personal?.dob);
  const incidentDate = getInputDate(incident?.callTime);
  const incidentTime = getInputTime(incident?.callTime);
  const incidentMeridiem = getAmPm(incident?.callTime);
  const defaultRequestDueDate = getLocalMoment(incident?.callTime)
    ?.clone?.()
    ?.add(1, "week")
    ?.format("YYYY-MM-DD");
  const bacValue =
    arresteePis?.bac === null || arresteePis?.bac === undefined
      ? ""
      : String(arresteePis.bac);
  const bacNumber = parseBacValue(bacValue);
  const isCdl = !!(personal?.cdl || arresteePerson?.cdl || arresteePis?.cdl);

  return {
    incidentNumber: incident?.incidentNumber || "",
    unit: "",
    department: incident?.ori?.oriName || "",
    dateOfIncident: incidentDate,
    timeOfIncident: incidentTime,
    timeOfIncidentMeridiem: incidentMeridiem,
    name: formatNameFromPersonObj(arresteePerson),
    driversLicenseNo: personal?.driversLicense || "",
    driversLicenseState: personal?.licenseState?.name || "",
    mailingStreet: buildStreetLine(mailingAddress),
    isCdl,
    isCommercialVehicle: false,
    isHazmat: false,
    townCity: mailingAddress?.city || "",
    mailingState: mailingAddress?.state || "",
    zipCode: mailingAddress?.zip || "",
    socialSecurityNumber: "",
    usDotNumber: "",
    age: formatAge(getAgeInYears(personal?.dob, incident?.callTime)),
    dateOfBirth: dateOfBirthMoment?.isValid?.()
      ? dateOfBirthMoment.format("YYYY-MM-DD")
      : "",
    placeOfBirth: personal?.pobAddress?.name || "",
    phoneHome: personal?.phone || "",
    phoneCell: personal?.cell || "",
    phoneWork: personal?.work || "",
    isFirstViolation: false,
    isSecondOrSubsequentViolation: false,
    bacWithPreviousConviction: false,
    refusedBreathOrBloodTest: false,
    testResultAtOrAboveLimit: bacNumber !== null && bacNumber >= 0.08,
    alcoholConcentration: bacValue,
    commercialRefusal: false,
    commercialTestAtOrAboveLimit: false,
    commercialAlcoholConcentration: bacValue,
    suspendLicense: false,
    disqualifyCommercialDriver: false,
    suspensionBeginsOn: incidentDate,
    dateRequestDue: defaultRequestDueDate || "",
    circuitCounty: "",
    courtAddress: "",
    hearingDate: "",
    hearingTime: "",
    hearingMeridiem: "AM",
    noticeDeliveryMethod: "hand",
    noticeIssuedDate: incidentDate,
    affidavitDeliveryMethod: "included",
    officerName: formatIntentOfficerName(intentOfficer),
    officerNumber: getIntentOfficerNumber(intentOfficer),
  };
};

const ArrestInformation = ({
  arrest,
  piss,
  arrestTab,
  setArrestTab,
  arrestsLength,
  arrestIndex,
  incidentId,
  incidentIndex,
  userRoles,
  disabled = undefined,
  setPausePolling,
  incidentOfficers,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const classes = useStyles();
  const [incidentPeople, setIncidentPeople] = useState();
  const { activeOfficersError, activeOfficersLoading, user } =
    useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const { ADD_ARREST, REMOVE_ARREST, UPDATE_ARREST, ADD_CHARGE } =
    INCIDENT_ACTIONS;
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const {
    ibrMultipleArrestTypes,
    arresteeWeaponTypes: arresteeWeaponTypess,
    ibrArrestTypes,
    ibrClearanceCodes,
    ibrJuvenileDispositions,
  } = setup.setupLists;
  const {
    updateArrestInIncident,
    throttledUpdateArrestInIncident,
    removeArrestFromIncident,
  } = useContext(MutationContext);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [showIntentToSuspendModal, setShowIntentToSuspendModal] =
    useState(false);
  const [intentToSuspendData, setIntentToSuspendData] = useState({});
  const intentToSuspendRef = useRef(null);
  const intentReadOnlyInputProps = { readOnly: true };
  const intentReadOnlySelectProps = { readOnly: true };
  const intentReadOnlyFieldProps = {
    disabled: true,
  };
  const intentValidationErrors = useMemo(
    () => getIntentValidationErrors(intentToSuspendData),
    [intentToSuspendData],
  );
  const resolvedIntentToSuspendData = useMemo(
    () => getResolvedIntentToSuspendData(intentToSuspendData),
    [intentToSuspendData],
  );

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    let persons = [];
    piss.map(pis => {
      let person = {
        id: pis?.person?.id,
        name: `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`,
      };
      persons.push(person);
      return null;
    });

    setIncidentPeople(persons);
  }, [arrest?.arrestee, piss]);

  // const handleAddCharge = () => {
  //   const emptyCharge = createEmptyArrestCharge();

  //   incidentDispatch({
  //     type: ADD_CHARGE,
  //     payload: {
  //       incidentId: incidentId,
  //       arrestId: arrest?.id,
  //       charge: emptyCharge,
  //     },
  //   });
  //   throttledUpdateChargeInArrest({
  //     variables: {
  //       input: {
  //         incidentArrestId: arrest?.id,
  //         arrestChargeId: emptyCharge?.id,
  //         originatorId: user?.id,
  //       },
  //     },
  //   });
  // };

  useEffect(() => {
    if (
      arrest?.id &&
      (arrest?.arrestCharges?.length === 0 || !arrest?.arrestCharges)
    ) {
      console.log("Auto adding charge to new arrest");
      const emptyCharge = createEmptyArrestCharge();
      incidentDispatch({
        type: ADD_CHARGE,
        payload: {
          incidentId: incidentId,
          arrestId: arrest?.id,
          charge: emptyCharge,
        },
      });
    }
  }, [arrest, incidentId, incidentDispatch, ADD_CHARGE]);

  const stateToMutation = {
    arrestee: "arresteeId",
    arrestor: "arrestorId",
    arrestedAt: "arrestedAt",
    ibrMultipleArrestType: "ibrMultipleArrestTypeId",
    arrestLocation: "arrestLocationId",
    citedAt: "citedAt",
    arresteeWeaponTypes: "arresteeWeaponTypeIds",
    ibrArrestType: "ibrArrestTypeId",
    ibrClearanceCode: "ibrClearanceCodeId",
    exceptionClearedAt: "exceptionClearedAt",
    ibrJuvenileDisposition: "ibrJuvenileDispositionId",
    childUnderEighteen: "childUnderEighteen",
    printsPhotosNeeded: "printsPhotosNeeded",
  };

  const handleChange = event => {
    const name = event.target.name || null;
    const value = event.target.value;
    const mutationValue = getValueFromEventValue(event.target.value);
    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_ARREST,
      payload: {
        incidentId: incidentId,
        arrestId: arrest.id ? arrest.id : arrest.uuid,
        property: name,
        value: value,
      },
    });
    if (name === "arresteeWeaponTypes") {
      updateArrestInIncident({
        variables: {
          input: {
            incidentArrestId: arrest.id,
            incidentId: incidentId,
            originatorId: user?.id,
            [fieldToUpdate]: parseObjectArrayIdString(value),
          },
        },
      });
    } else {
      updateArrestInIncident({
        variables: {
          input: {
            incidentArrestId: arrest.id,
            incidentId: incidentId,
            originatorId: user?.id,
            [fieldToUpdate]: mutationValue,
          },
        },
      });
    }
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
  };

  const handleCheckbox = e => {
    const bool = e.target.checked;
    const name = e.target.value;
    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_ARREST,
      payload: {
        incidentId: incidentId,
        arrestId: arrest.id ? arrest.id : arrest.uuid,
        property: name,
        value: bool,
      },
    });
    updateArrestInIncident({
      variables: {
        input: {
          incidentArrestId: arrest.id,
          incidentId: incidentId,
          originatorId: user?.id,
          [fieldToUpdate]: bool,
        },
      },
    });
  };

  const updateAddress = address => {
    incidentDispatch({
      type: UPDATE_ARREST,
      payload: {
        incidentId: incidentId,
        arrestId: arrest.id ? arrest.id : arrest.uuid,
        property: "location",
        value: address,
      },
    });
    updateArrestInIncident({
      variables: {
        input: {
          incidentArrestId: arrest.id,
          incidentId: incidentId,
          originatorId: user?.id,
          locationId: address?.id ? address.id : "",
        },
      },
    });
    // incidentDispatch({ type: UPDATE_INCIDENT, payload: { id: incidentId ? incidentId : incident.uuid, property: 'address', value: address } })
    // updateIncidentAddress({ variables: { input: { incidentId: incidentId ? incidentId : incident.uuid, addressId: address.id, originatorId: user?.id } } })
  };

  const handleDateChange = (value, name, date) => {
    const fieldToUpdate = stateToMutation[name];
    const parsedDate = parseDateFlexible(date);
    if (!date) {
      incidentDispatch({
        type: UPDATE_ARREST,
        payload: {
          incidentId: incidentId,
          arrestId: arrest.id ? arrest.id : arrest.uuid,
          property: name,
          value: "",
        },
      });
      updateArrestInIncident({
        variables: {
          input: {
            incidentArrestId: arrest.id,
            incidentId: incidentId,
            originatorId: user?.id,
            [fieldToUpdate]: "",
          },
        },
      });
    }
    //add updateIncident mutation here like the person one down below
    //  updatePersonDob({ variables: { input: { personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid, [fieldToUpdate]: serverTimeFormat } } })
    if (parsedDate?.isValid()) {
      const localTimeString = parsedDate.format("YYYY-MM-DD HH:mm:ss");
      const serverTimeFormat = parsedDate;
      incidentDispatch({
        type: UPDATE_ARREST,
        payload: {
          incidentId: incidentId,
          arrestId: arrest.id ? arrest.id : arrest.uuid,
          property: name,
          value: localTimeString,
        },
      });
      updateArrestInIncident({
        variables: {
          input: {
            incidentArrestId: arrest.id,
            incidentId: incidentId,
            originatorId: user?.id,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
      });
      //add updateIncident mutation here like the person one down below
      //  updatePersonDob({ variables: { input: { personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid, [fieldToUpdate]: serverTimeFormat } } })
    }
  };

  const handleAutoCompleteChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_ARREST,
      payload: {
        incidentId: incidentId,
        arrestId: arrest.id ? arrest.id : arrest.uuid,
        property: name,
        value: value,
      },
    });
    updateArrestInIncident({
      variables: {
        input: {
          incidentArrestId: arrest.id,
          incidentId: incidentId,
          originatorId: user?.id,
          [fieldToUpdate]: value?.id ? value.id : value,
        },
      },
    });
  };

  const handleRemoveArrest = arrestId => {
    if (
      window.confirm(`Are you sure you want to remove Arrest ${arrest.id}?`)
    ) {
      incidentDispatch({
        type: REMOVE_ARREST,
        payload: {
          incidentId: incidentId,
          arrestId: arrestId,
        },
      });
      setArrestTab(arrestTab > 0 ? arrestTab - 1 : 0);
      removeArrestFromIncident({
        variables: {
          input: { incidentArrestId: arrestId, originatorId: user?.id },
        },
      });
      if (arrestsLength <= 1) {
        handleAddArrestToIncident(0);
      }
      return null;
    }
  };

  const handleAddArrestToIncident = (focusTab = null) => {
    const arrestUuid = handleUuid();
    const input = {
      incidentId: incidentId,
      incidentArrestId: arrestUuid,
      originatorId: user?.id,
    };

    const emptyArrest = {
      id: arrestUuid,
      uuid: arrestUuid,
    };
    incidentDispatch({
      type: ADD_ARREST,
      payload: {
        incidentId: incidentId,
        arrest: emptyArrest,
      },
    });
    throttledUpdateArrestInIncident({ variables: { input: input } });
    const tab = focusTab ?? arrestsLength;
    setArrestTab(tab);
  };

  const address = useMemo(() => {
    return {
      addressName: arrest?.location ? arrest?.location.name : "",
      streetNumber: arrest?.location ? arrest?.location.streetNumber : "",
      streetName: arrest?.location ? arrest?.location.streetName : "",
      unit: arrest?.location ? arrest?.location.unit : "",
      city: arrest?.location ? arrest?.location.city : "",
      state: arrest?.location ? arrest?.location.state : "",
      zip: arrest?.location ? arrest?.location.zip : "",
      lat: arrest?.location ? arrest?.location.lat : "",
      lng: arrest?.location ? arrest?.location.lng : "",
      validAddressAlerts: arrest?.location
        ? arrest?.location?.validAddressAlerts
        : "",
      id: arrest?.location ? arrest?.location?.id : "",
    };
  }, [arrest?.location]);

  const currentIncident = incidents?.[incidentIndex] || {};
  const arresteePis = useMemo(() => {
    const arresteeId = arrest?.arrestee?.id?.toString();
    if (!arresteeId) return null;
    return (
      piss?.find(pis => pis?.person?.id?.toString() === arresteeId) || null
    );
  }, [arrest?.arrestee?.id, piss]);
  const arresteePerson = useMemo(() => {
    return arresteePis?.person || null;
  }, [arresteePis]);

  const openIntentToSuspendModal = () => {
    setIntentToSuspendData(
      getIntentDefaults({
        incident: currentIncident,
        arresteePerson,
        arresteePis,
        arrest,
        user,
      }),
    );
    setShowIntentToSuspendModal(true);
  };

  const closeIntentToSuspendModal = () => {
    setShowIntentToSuspendModal(false);
  };

  const updateIntentToSuspendField = (field, value) => {
    setIntentToSuspendData(prev => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "alcoholConcentration") {
        next.commercialAlcoholConcentration = value || "";
      }

      if (field === "commercialAlcoholConcentration") {
        next.alcoholConcentration = value || "";
      }

      if (shouldAutoCheckIntentBacResult(next)) {
        next.testResultAtOrAboveLimit = true;
      }

      if (shouldAutoCheckCommercialBacResult(next)) {
        next.commercialTestAtOrAboveLimit = true;
      }

      if (
        field === "testResultAtOrAboveLimit" &&
        shouldAutoCheckIntentBacResult(next)
      ) {
        next.testResultAtOrAboveLimit = true;
      }

      if (
        field === "commercialTestAtOrAboveLimit" &&
        shouldAutoCheckCommercialBacResult(next)
      ) {
        next.commercialTestAtOrAboveLimit = true;
      }

      return next;
    });
  };

  const updateIntentBooleanFromSelect = (field, value) => {
    updateIntentToSuspendField(field, value === "yes");
  };

  const printIntentToSuspend = () => {
    if (intentValidationErrors.length > 0) {
      return;
    }

    const element = intentToSuspendRef.current;
    if (!element) {
      console.error("No intent-to-suspend element to print");
      return;
    }

    const shadowRoot = element.shadowRoot;
    if (!shadowRoot) {
      console.error("No rendered intent-to-suspend content to print");
      return;
    }
    const styleTag = shadowRoot.querySelector("style");
    const pageStyles = styleTag?.textContent || "";
    const printStyles = pageStyles.replace(/:host\b/g, ".intent-print-root");
    const pageMarkup = Array.from(shadowRoot.children)
      .filter(node => node.tagName !== "STYLE")
      .map(node => node.outerHTML)
      .join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Intent To Suspend</title>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
            }
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          </style>
          <style>${printStyles}</style>
        </head>
        <body>
          <div class="intent-print-root">${pageMarkup}</div>
        </body>
      </html>
    `);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  if (incidentPeople) {
    return (
      <div
        data-testid="arrest-information-container"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "flex-start",
          }}
        >
          <SingleDropDown
            handleChange={handleChange}
            stateName={"arrestee"}
            value={
              incidentPeople?.length > 0
                ? incidentPeople.find(
                    person =>
                      person.id?.toString() ===
                      arrest?.arrestee?.id?.toString(),
                  )
                : ""
            }
            options={incidentPeople}
            title={"Arrestee"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <CustomAutoComplete
            title={"Arresting\xa0Officer"}
            loading={activeOfficersLoading}
            options={incidentOfficers}
            error={activeOfficersError}
            value={arrest?.arrestor}
            stateName={"arrestor"}
            updateFields={handleAutoCompleteChange}
            nameFormatter={formatOfficerName}
            userRoles={userRoles}
            {...props}
          />

          <FormControl variant="standard" className={classes.formControl}>
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={classes.smallMatchMargin}
              clearable={true}
              data-cy="arrested-date-time"
              disabled={memoizedLocked}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Arrest Date/Time"
              name="arrestedAt"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(arrest?.arrestedAt)}
            />
          </FormControl>
          <SingleDropDown
            handleChange={handleChange}
            stateName={"ibrMultipleArrestType"}
            value={arrest?.ibrMultipleArrestType}
            options={ibrMultipleArrestTypes}
            title={"IBR Mult Arrest Indicator"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <AddressInputAutoSuggest
            address={address}
            id={incidentId}
            setAddress={updateAddress}
            title={"Arrest\xa0Location"}
            userRoles={userRoles}
            pauseLocationPolling={() => setPausePolling(true)}
            resumeLocationPolling={() => setPausePolling(false)}
            {...props}
          />
          <FormControl variant="standard" className={classes.formControl}>
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={classes.smallMatchMargin}
              clearable={true}
              data-cy="cited-date-time"
              disabled={memoizedLocked}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Citation Date/Time"
              name="citedAt"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(arrest?.citedAt)}
            />
          </FormControl>
          <MultiDropDownObjects
            id={"arresteeWeaponTypes"}
            title={"IBR Arrestee Weapon"}
            stateName={"arresteeWeaponTypes"}
            handleChange={handleChange}
            selectedValues={
              arrest?.arresteeWeaponTypes
                ? arresteeWeaponTypess.filter(option =>
                    arrest?.arresteeWeaponTypes.some(
                      type => option.id === type.id,
                    ),
                  )
                : []
            }
            options={arresteeWeaponTypess}
            formStyle={classes.formControl}
            labelStyle={classes.multiInputLabel}
            selectStyle={null}
            chipsStyle={classes.chips}
            chipStyle={classes.chip}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            handleChange={handleChange}
            stateName={"ibrArrestType"}
            value={arrest?.ibrArrestType}
            options={ibrArrestTypes}
            title={"IBR Arrest Type"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <SingleDropDown
            handleChange={handleChange}
            stateName={"ibrClearanceCode"}
            value={arrest?.ibrClearanceCode}
            options={ibrClearanceCodes}
            title={"IBR Exceptional Clearance Code"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <FormControl variant="standard" className={classes.formControl}>
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={classes.smallMatchMargin}
              clearable={true}
              data-cy="exceptional-clearance-date-time"
              disabled={memoizedLocked}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Exceptional Clearance Date"
              name="exceptionClearedAt"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(arrest?.exceptionClearedAt)}
            />
          </FormControl>
          <SingleDropDown
            handleChange={handleChange}
            stateName={"ibrJuvenileDisposition"}
            value={arrest?.ibrJuvenileDisposition}
            options={ibrJuvenileDispositions}
            title={"Juvenile Disposition"}
            classStyle={classes.formControl}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            checked={arrest?.childUnderEighteen || false}
            stateName={"childUnderEighteen"}
            onChange={handleCheckbox}
            title={"Has Child Under 18"}
            formStyle={classes.formControl}
            userRoles={userRoles}
            {...props}
          />
          <CheckboxWithLabel
            checked={arrest?.printsPhotosNeeded || false}
            stateName={"printsPhotosNeeded"}
            onChange={handleCheckbox}
            title={"Prints & Photos Needed"}
            formStyle={classes.formControl}
            userRoles={userRoles}
            {...props}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {arrest?.arrestCharges?.map((charge, index) => {
            return (
              <Charge
                key={charge.id}
                numOfCharges={arrest?.arrestCharges.length}
                charge={charge}
                chargeIndex={index}
                incidentId={incidentId}
                incidentIndex={incidentIndex}
                incidentOffenses={
                  incidents[incidentIndex]?.incidentDetail?.incidentOffenses
                }
                offenseVios={incidents[
                  incidentIndex
                ]?.incidentDetail?.incidentOffenses
                  ?.map(io => io?.offenseViolationPrototype)
                  ?.filter(a => !!a)}
                arrestIndex={arrestIndex}
                arrestId={arrest.id}
                userRoles={userRoles}
                {...props}
              />
            );
          })}
        </div>
        <Modal
          open={showIntentToSuspendModal}
          onClose={closeIntentToSuspendModal}
          className={classes.ticketModal}
          id="intent-to-suspend-modal"
          keepMounted={false}
        >
          <div className={classes.dmvModals}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderBottom: "1px solid black",
              }}
            >
              <h3>Intent To Suspend</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="contained"
                  onClick={printIntentToSuspend}
                  disabled={intentValidationErrors.length > 0}
                >
                  Print
                </Button>
                <Button variant="contained" onClick={closeIntentToSuspendModal}>
                  Close
                </Button>
              </div>
            </div>
            <div
              style={{
                overflow: "auto",
                padding: "12px",
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "10px",
              }}
            >
              {intentValidationErrors.length > 0 && (
                <Box
                  style={{
                    gridColumn: "1 / -1",
                    border: "1px solid #b00020",
                    color: "#b00020",
                    padding: "8px",
                  }}
                >
                  <strong>Resolve these Intent to Suspend issues before printing:</strong>
                  <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                    {intentValidationErrors.map(error => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </Box>
              )}
              <TextField
                label="Incident No."
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.incidentNumber || ""}
                onChange={e =>
                  updateIntentToSuspendField("incidentNumber", e.target.value)
                }
              />
              <TextField
                label="Unit"
                variant="outlined"
                size="small"
                value={intentToSuspendData.unit || ""}
                onChange={e =>
                  updateIntentToSuspendField("unit", e.target.value)
                }
              />
              <TextField
                label="Department"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.department || ""}
                onChange={e =>
                  updateIntentToSuspendField("department", e.target.value)
                }
              />
              <TextField
                label="Date of Incident"
                variant="outlined"
                size="small"
                type="date"
                {...intentReadOnlyFieldProps}
                InputLabelProps={{ shrink: true }}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.dateOfIncident || ""}
                onChange={e =>
                  updateIntentToSuspendField("dateOfIncident", e.target.value)
                }
              />
              <TextField
                label="Time"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.timeOfIncident || ""}
                onChange={e =>
                  updateIntentToSuspendField("timeOfIncident", e.target.value)
                }
              />
              <TextField
                select
                label="Time Meridiem"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                SelectProps={intentReadOnlySelectProps}
                value={intentToSuspendData.timeOfIncidentMeridiem || "AM"}
                onChange={e =>
                  updateIntentToSuspendField(
                    "timeOfIncidentMeridiem",
                    e.target.value,
                  )
                }
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </TextField>

              <TextField
                label="Name"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.name || ""}
                onChange={e =>
                  updateIntentToSuspendField("name", e.target.value)
                }
              />
              <TextField
                label="Driver's License No."
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.driversLicenseNo || ""}
                onChange={e =>
                  updateIntentToSuspendField("driversLicenseNo", e.target.value)
                }
              />
              <TextField
                label="License State"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.driversLicenseState || ""}
                onChange={e =>
                  updateIntentToSuspendField(
                    "driversLicenseState",
                    e.target.value,
                  )
                }
              />
              <TextField
                label="Mailing Street"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.mailingStreet || ""}
                onChange={e =>
                  updateIntentToSuspendField("mailingStreet", e.target.value)
                }
              />
              <TextField
                select
                label="CDL"
                variant="outlined"
                size="small"
                value={intentToSuspendData.isCdl ? "yes" : "no"}
                onChange={e =>
                  updateIntentBooleanFromSelect("isCdl", e.target.value)
                }
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
              <TextField
                select
                label="Driving Commercial Vehicle"
                variant="outlined"
                size="small"
                value={intentToSuspendData.isCommercialVehicle ? "yes" : "no"}
                onChange={e =>
                  updateIntentBooleanFromSelect(
                    "isCommercialVehicle",
                    e.target.value,
                  )
                }
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
              <TextField
                select
                label="Hazardous Materials"
                variant="outlined"
                size="small"
                value={intentToSuspendData.isHazmat ? "yes" : "no"}
                onChange={e =>
                  updateIntentBooleanFromSelect("isHazmat", e.target.value)
                }
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
              <TextField
                label="Town/City"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.townCity || ""}
                onChange={e =>
                  updateIntentToSuspendField("townCity", e.target.value)
                }
              />
              <TextField
                label="State"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.mailingState || ""}
                onChange={e =>
                  updateIntentToSuspendField("mailingState", e.target.value)
                }
              />
              <TextField
                label="Zip Code"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.zipCode || ""}
                onChange={e =>
                  updateIntentToSuspendField("zipCode", e.target.value)
                }
              />
              <TextField
                label="Social Security Number"
                variant="outlined"
                size="small"
                value={intentToSuspendData.socialSecurityNumber || ""}
                onChange={e =>
                  updateIntentToSuspendField(
                    "socialSecurityNumber",
                    e.target.value,
                  )
                }
              />
              <TextField
                label="US DOT #"
                variant="outlined"
                size="small"
                value={intentToSuspendData.usDotNumber || ""}
                onChange={e =>
                  updateIntentToSuspendField("usDotNumber", e.target.value)
                }
              />
              <TextField
                label="Age"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.age || ""}
                onChange={e =>
                  updateIntentToSuspendField("age", e.target.value)
                }
              />
              <TextField
                label="Date of Birth"
                variant="outlined"
                size="small"
                type="date"
                {...intentReadOnlyFieldProps}
                InputLabelProps={{ shrink: true }}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.dateOfBirth || ""}
                onChange={e =>
                  updateIntentToSuspendField("dateOfBirth", e.target.value)
                }
              />
              <TextField
                label="Place of Birth"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.placeOfBirth || ""}
                onChange={e =>
                  updateIntentToSuspendField("placeOfBirth", e.target.value)
                }
              />
              <TextField
                label="Home Phone"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.phoneHome || ""}
                onChange={e =>
                  updateIntentToSuspendField("phoneHome", e.target.value)
                }
              />
              <TextField
                label="Cell Phone"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.phoneCell || ""}
                onChange={e =>
                  updateIntentToSuspendField("phoneCell", e.target.value)
                }
              />

              <Box
                style={{ gridColumn: "1 / -1", borderTop: "1px solid #ccc" }}
              />
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.isFirstViolation}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "isFirstViolation",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  This is your first violation of 23 V.S.A. section 1201:
                  Effective date of suspension is set forth below unless a
                  hearing is requested.
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.isSecondOrSubsequentViolation}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "isSecondOrSubsequentViolation",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  This is your 2nd or subsequent violation of 23 V.S.A. section
                  1201 or 2nd or subsequent suspension under 23 V.S.A. section
                  1205 after July 1, 1991: Effective date of suspension is set
                  forth below, even if you request a hearing.
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.bacWithPreviousConviction}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "bacWithPreviousConviction",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  You operated a motor vehicle with a blood alcohol
                  concentration (BAC) of 0.02% or more after previous conviction
                  within the preceding three years with a BAC for the second or
                  subsequent was proven to be 0.16% or greater pursuant to 23
                  V.S.A. 1201(d)(2).
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.refusedBreathOrBloodTest}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "refusedBreathOrBloodTest",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  You refused to submit to the breath or blood test (license
                  suspension of at least six months).
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <Checkbox
                  checked={
                    !!intentToSuspendData.testResultAtOrAboveLimit ||
                    shouldAutoCheckIntentBacResult(intentToSuspendData)
                  }
                  onChange={e =>
                    updateIntentToSuspendField(
                      "testResultAtOrAboveLimit",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  The results of the breath or blood test show that your alcohol
                  concentration was
                </span>
                <TextField
                  label="BAC"
                  variant="outlined"
                  size="small"
                  style={{ width: "130px" }}
                  {...intentReadOnlyFieldProps}
                  InputProps={intentReadOnlyInputProps}
                  value={intentToSuspendData.alcoholConcentration || ""}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "alcoholConcentration",
                      e.target.value,
                    )
                  }
                />
                <span>
                  or more, equal to or more than 0.08%, or 0.02% if operating a
                  school bus or after previous conviction of 0.16% or greater
                  (license suspension of at least 90 days).
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.commercialRefusal}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "commercialRefusal",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  You operated a commercial vehicle and refused to submit to the
                  breath or blood test.
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <Checkbox
                  checked={
                    !!intentToSuspendData.commercialTestAtOrAboveLimit ||
                    shouldAutoCheckCommercialBacResult(intentToSuspendData)
                  }
                  onChange={e =>
                    updateIntentToSuspendField(
                      "commercialTestAtOrAboveLimit",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                  You operated a commercial vehicle and submitted to a breath or
                  blood test which shows your alcohol concentration was
                </span>
                <TextField
                  label="Commercial BAC"
                  variant="outlined"
                  size="small"
                  style={{ width: "170px" }}
                  {...intentReadOnlyFieldProps}
                  InputProps={intentReadOnlyInputProps}
                  value={intentToSuspendData.alcoholConcentration || ""}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "alcoholConcentration",
                      e.target.value,
                    )
                  }
                />
                <span>
                  or more while operating a commercial vehicle, equal to or more
                  than 0.04%.
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.suspendLicense}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "suspendLicense",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span>
                                      SUSPEND your driver&apos;s license or privilege to operate a motor
                  vehicle.
                </span>
              </Box>
              <Box
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  flexWrap: "nowrap",
                }}
              >
                <Checkbox
                  checked={!!intentToSuspendData.disqualifyCommercialDriver}
                  onChange={e =>
                    updateIntentToSuspendField(
                      "disqualifyCommercialDriver",
                      e.target.checked,
                    )
                  }
                  color="primary"
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  DISQUALIFY you as a commercial driver for a period of at least
                  one year (if first offense) or for your lifetime (if this is
                  your second or subsequent offense) and until all requirements
                  are met for license reinstatement.
                </span>
              </Box>
              <TextField
                label="Suspension Begins"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={intentToSuspendData.suspensionBeginsOn || ""}
                onChange={e =>
                  updateIntentToSuspendField(
                    "suspensionBeginsOn",
                    e.target.value,
                  )
                }
              />
              <TextField
                label="Date Request Due"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={intentToSuspendData.dateRequestDue || ""}
                onChange={e =>
                  updateIntentToSuspendField("dateRequestDue", e.target.value)
                }
              />
              <TextField
                label="Circuit/County"
                variant="outlined"
                size="small"
                value={intentToSuspendData.circuitCounty || ""}
                onChange={e =>
                  updateIntentToSuspendField("circuitCounty", e.target.value)
                }
              />
              <TextField
                label="Court Address"
                variant="outlined"
                size="small"
                value={intentToSuspendData.courtAddress || ""}
                onChange={e =>
                  updateIntentToSuspendField("courtAddress", e.target.value)
                }
              />
              <TextField
                label="Hearing Date"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={intentToSuspendData.hearingDate || ""}
                onChange={e =>
                  updateIntentToSuspendField("hearingDate", e.target.value)
                }
              />
              <TextField
                label="Hearing Time"
                variant="outlined"
                size="small"
                value={intentToSuspendData.hearingTime || ""}
                onChange={e =>
                  updateIntentToSuspendField("hearingTime", e.target.value)
                }
              />
              <TextField
                select
                label="Hearing Meridiem"
                variant="outlined"
                size="small"
                value={intentToSuspendData.hearingMeridiem || "AM"}
                onChange={e =>
                  updateIntentToSuspendField("hearingMeridiem", e.target.value)
                }
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </TextField>
              <TextField
                select
                label="Notice Delivery"
                variant="outlined"
                size="small"
                value={intentToSuspendData.noticeDeliveryMethod || "hand"}
                onChange={e =>
                  updateIntentToSuspendField(
                    "noticeDeliveryMethod",
                    e.target.value,
                  )
                }
              >
                <MenuItem value="hand">Hand Delivered</MenuItem>
                <MenuItem value="mail">Mailed</MenuItem>
              </TextField>
              <TextField
                label="Notice Issued Date"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={intentToSuspendData.noticeIssuedDate || ""}
                onChange={e =>
                  updateIntentToSuspendField("noticeIssuedDate", e.target.value)
                }
              />
              <TextField
                select
                label="Affidavit Delivery"
                variant="outlined"
                size="small"
                value={
                  intentToSuspendData.affidavitDeliveryMethod || "included"
                }
                onChange={e =>
                  updateIntentToSuspendField(
                    "affidavitDeliveryMethod",
                    e.target.value,
                  )
                }
              >
                <MenuItem value="included">Included with Notice</MenuItem>
                <MenuItem value="later">Mailed Later</MenuItem>
              </TextField>
              <TextField
                label="Officer Name"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.officerName || ""}
                onChange={e =>
                  updateIntentToSuspendField("officerName", e.target.value)
                }
              />
              <TextField
                label="Officer Number"
                variant="outlined"
                size="small"
                {...intentReadOnlyFieldProps}
                InputProps={intentReadOnlyInputProps}
                value={intentToSuspendData.officerNumber || ""}
                onChange={e =>
                  updateIntentToSuspendField("officerNumber", e.target.value)
                }
              />
            </div>
            <div style={{ display: "none" }}>
              <intent-to-suspend
                ref={intentToSuspendRef}
                form-data={JSON.stringify(resolvedIntentToSuspendData)}
              />
            </div>
          </div>
        </Modal>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-around"
          style={{ width: "100%" }}
        >
          <Button
            style={{
              width: "fit-content",
              marginTop: "3vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            variant="contained"
            onClick={openIntentToSuspendModal}
          >
            Intent To Suspend
          </Button>
          <Button
            style={{
              width: "fit-content",
              marginTop: "3vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            disabled={memoizedLocked}
            variant="contained"
            onClick={() => handleRemoveArrest(arrest.id)}
          >
            Delete Arrest
          </Button>
          <Button
            style={{
              width: "fit-content",
              marginTop: "3vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            disabled={memoizedLocked}
            variant="contained"
            onClick={() => handleAddArrestToIncident(null)}
          >
            Add Arrest
          </Button>
        </Box>
      </div>
    );
  } else {
    return (
      <div>
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
};

export {
  getIntentDefaults,
  getIntentValidationErrors,
  getResolvedIntentToSuspendData,
  parseBacValue,
};

export default ArrestInformation;
